// AI-CONTEXT: See docs/features/portfolio-maatschappelijke-waarde.md
// AI-CONTEXT: See docs/features/participation-value-dashboard-card.md
import { useMemo } from 'react'
import { Link } from 'react-router-dom'
import { client } from '../../hooks/Client'
import formatEuro from '../../helpers/formatEuro'
import usePortfolioSocietalValue from '../../hooks/portfolio/usePortfolioSocietalValue'
import PageHeader from '../../components/layout/PageHeader'
import Table from '../../components/common/table/Table'
import TableCell from '../../components/common/table/TableCell'

const COLUMNS = [
    { key: 'name', label: 'PROJECT' },
    { key: 'participation', label: 'PARTICIPATIELADDER', searchable: false },
    { key: 'msi', label: 'MEETSTANDAARD', searchable: false },
    { key: 'materials', label: 'MATERIALEN', searchable: false },
    { key: 'total', label: 'TOTAAL', searchable: false },
]

/**
 * De Maatschappelijke waarde van een portfolio, uitgesplitst per project en per
 * stroom.
 *
 * De drie stromen worden apart berekend en pas hier opgeteld — dezelfde
 * volgorde en dezelfde namen als op de detailpagina van één organisatie, zodat
 * een beheerder die tussen de twee heen en weer gaat hetzelfde verhaal leest.
 * AI-CONTEXT: See docs/methodiek-impact-per-euro.md
 *
 * De cijfers komen uit de nachtrun. Een project waarvoor nog niets is
 * uitgerekend telt NIET als nul mee: het staat apart genoemd, want anders is
 * het totaal stil te laag.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 */
const PortfolioValueDetail = () => {
    const { data, loading, error } = usePortfolioSocietalValue()

    const projects = useMemo(() => (Array.isArray(data?.projects) ? data.projects : []), [data])

    // Eén optelling voor de voetregel, over dezelfde cellen als de tabel toont.
    // Een apart totaal per kolom zou van de rijen kunnen afwijken zonder dat
    // iets het laat zien.
    const kolomtotalen = useMemo(() => projects.reduce((t, p) => ({
        participation: t.participation + p.Participation,
        msi: t.msi + p.Msi,
        materials: t.materials + p.Materials,
        total: t.total + p.Total,
    }), { participation: 0, msi: 0, materials: 0, total: 0 }), [projects])

    const aantalZonder = Array.isArray(data?.pending) ? data.pending.length : 0

    const berekendOp = data?.computedAt
        ? new Date(data.computedAt).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' })
        : ''

    return (
        <div className='main'>
            <div className='main-container'>
                <PageHeader
                    heading='Maatschappelijke waarde van je projecten'
                    subHeading=''
                    description=''
                    AI={false}
                />

                {error ? (
                    <p className='participation-value-error'>
                        We konden de waarde van je projecten niet ophalen. Ververs de pagina of probeer het later opnieuw.
                    </p>
                ) : loading ? (
                    <p className='participation-value-note'>De bedragen worden opgehaald…</p>
                ) : (
                    <>
                        {berekendOp &&
                            <p className='participation-value-note'>
                                Bijgewerkt op {berekendOp}. De bedragen worden elke nacht opnieuw berekend.
                            </p>
                        }

                        {aantalZonder > 0 &&
                            <p className='participation-value-note'>
                                Voor {aantalZonder} {aantalZonder === 1 ? 'project' : 'projecten'} is nog geen bedrag
                                berekend. {aantalZonder === 1 ? 'Dat project staat' : 'Die projecten staan'} niet in
                                deze tabel en {aantalZonder === 1 ? 'telt' : 'tellen'} niet mee in het totaal.
                            </p>
                        }

                        {/* De regel hierboven zegt al dat er projecten wachten op een
                            berekening. Daar 'er hangen nog geen projecten onder dit
                            portfolio' onder zetten spreekt dat tegen: allebei los waar,
                            samen onzin. Alleen als er ook niets in de wacht staat is die
                            tweede zin de waarheid.
                            AI-CONTEXT: See docs/pitfalls.md#ui-independent-badges-contradict-each-other */}
                        {projects.length === 0 ? (
                            aantalZonder === 0 && (
                                <p className='participation-value-note'>
                                    Er hangen nog geen projecten onder dit portfolio met een berekend bedrag.
                                </p>
                            )
                        ) : (
                            <div className='dashboard-container'>
                                <Table
                                    columns={COLUMNS}
                                    items={projects}
                                    getItemKey={(project) => project.CompagnyID}
                                    searchAccessor={(project) => project.Name || ''}
                                    searchPlaceholder='Zoeken op project'
                                    emptyMessage='Geen projecten gevonden.'
                                    renderRow={(project) => (
                                        <>
                                            <TableCell>
                                                <Link to={`/${client}/portfolioprojectdetails/${project.CompagnyID}`}>
                                                    {project.Name || '—'}
                                                </Link>
                                            </TableCell>
                                            <TableCell>{formatEuro(project.Participation)}</TableCell>
                                            <TableCell>{formatEuro(project.Msi)}</TableCell>
                                            <TableCell>{formatEuro(project.Materials)}</TableCell>
                                            <TableCell>{formatEuro(project.Total)}</TableCell>
                                        </>
                                    )}
                                    footerRow={(
                                        <>
                                            <TableCell>Totaal</TableCell>
                                            <TableCell>{formatEuro(kolomtotalen.participation)}</TableCell>
                                            <TableCell>{formatEuro(kolomtotalen.msi)}</TableCell>
                                            <TableCell>{formatEuro(kolomtotalen.materials)}</TableCell>
                                            <TableCell>{formatEuro(kolomtotalen.total)}</TableCell>
                                        </>
                                    )}
                                />
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}

export default PortfolioValueDetail
